import { useState, useEffect, useCallback } from 'react';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { WaterPipeCalculator } from './components/WaterPipeCalculator';
import { CopperPipeCalculator } from './components/CopperPipeCalculator';
import { FlowRateCalculator } from './components/FlowRateCalculator';
import { PressureDropCalculator } from './components/PressureDropCalculator';
import { VelocityCalculator } from './components/VelocityCalculator';
import { ReferenceTables } from './components/ReferenceTables';
import { EngineeringGuides } from './components/EngineeringGuides';
import {
  PrintableReport,
  WaterPipeReportData,
  CopperPipeReportData,
  FlowRateReportData,
  PressureDropReportData,
  VelocityReportData,
  ActiveCalculatorReport,
} from './components/PrintableReport';
import { ExploreCalculatorsSection } from './components/ExploreCalculatorsSection';
import { ArticleFormula } from './components/ArticleFormula';
import { ArticleDarcy } from './components/ArticleDarcy';
import { AboutPage } from './components/AboutPage';
import { ContactPage } from './components/ContactPage';
import { PrivacyPolicyPage } from './components/PrivacyPolicyPage';
import { TermsPage } from './components/TermsPage';
import { DisclaimerPage } from './components/DisclaimerPage';
import { BlogHomePage } from './components/blog/BlogHomePage';
import { BlogArticlePage } from './components/blog/BlogArticlePage';
import { getArticleBySlug, getAllArticles } from './data/blog';
import { useLanguage } from './i18n/LanguageContext';
import { homeTranslations } from './i18n/homeTranslations';
import { getPathWithoutLanguage, getLocalizedPath } from './i18n/languages';

export const CALCULATOR_ROUTES: Record<string, string> = {
  water: '/',
  copper: '/copper-pipe-sizing/',
  flow: '/flow-rate-calculator/',
  pressure: '/pressure-drop-calculator/',
  velocity: '/pipe-velocity-standards/',
};

function getInitialRouteState(): { view: string; tab: string; articleSlug?: string } {
  if (typeof window === 'undefined') {
    return { view: 'calculators', tab: 'water' };
  }

  const cleanPath = getPathWithoutLanguage(window.location.pathname)
    .toLowerCase()
    .replace(/\/+$/, '') || '/';

  // Engineering Blog Routes
  if (cleanPath === '/blog' || cleanPath === 'blog') {
    return { view: 'blog-home', tab: 'water' };
  }
  if (cleanPath.startsWith('/blog/') || cleanPath.startsWith('blog/')) {
    const slug = cleanPath.replace(/^\/?blog\/?/, '').replace(/\/+$/, '');
    if (slug) {
      return { view: 'blog-article', tab: 'water', articleSlug: slug };
    }
    return { view: 'blog-home', tab: 'water' };
  }

  // Dedicated Calculator Canonical & Legacy Routes
  if (cleanPath.includes('copper-pipe-sizing')) {
    return { view: 'calculators', tab: 'copper' };
  }
  if (cleanPath.includes('flow-rate') || cleanPath.includes('pipe-flow-rate')) {
    return { view: 'calculators', tab: 'flow' };
  }
  if (cleanPath.includes('pressure-drop') || cleanPath.includes('pipe-pressure-drop')) {
    return { view: 'calculators', tab: 'pressure' };
  }
  if (cleanPath.includes('velocity-standards') || cleanPath.includes('pipe-velocity')) {
    return { view: 'calculators', tab: 'velocity' };
  }
  if (cleanPath.includes('water-pipe-sizing')) {
    return { view: 'calculators', tab: 'water' };
  }

  // Engineering Tables & Guides
  if (cleanPath.includes('pipe-size-charts')) return { view: 'tables', tab: 'water' };
  if (cleanPath.includes('pipe-sizing-formula')) return { view: 'formula', tab: 'water' };
  if (cleanPath.includes('darcy-weisbach-equation')) return { view: 'darcy', tab: 'water' };
  if (cleanPath.includes('guides')) return { view: 'guides', tab: 'water' };

  // Informational & Legal Pages
  if (cleanPath.includes('about')) return { view: 'about', tab: 'water' };
  if (cleanPath.includes('contact')) return { view: 'contact', tab: 'water' };
  if (cleanPath.includes('privacy')) return { view: 'privacy', tab: 'water' };
  if (cleanPath.includes('terms')) return { view: 'terms', tab: 'water' };
  if (cleanPath.includes('disclaimer')) return { view: 'disclaimer', tab: 'water' };

  return { view: 'calculators', tab: 'water' };
}

export default function App() {
  const initial = getInitialRouteState();
  const { language } = useLanguage();

  const tx = homeTranslations[language] ?? homeTranslations.en;

  const [activeView, setActiveView] = useState<string>(initial.view);
  const [activeTab, setActiveTab] = useState<string>(initial.tab);
  const [activeSlug, setActiveSlug] = useState<string>(initial.articleSlug || '');
  const [unitSystem, setUnitSystem] = useState<'metric' | 'imperial'>('metric');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [activeReport, setActiveReport] = useState<ActiveCalculatorReport>(null);

  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('pipe-theme') === 'dark';
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
    localStorage.setItem('pipe-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Synchronize router state with browser Back/Forward
  useEffect(() => {
    const handlePopState = () => {
      const state = getInitialRouteState();
      setActiveView(state.view);
      setActiveTab(state.tab);
      if (state.articleSlug) setActiveSlug(state.articleSlug);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateTo = useCallback(
    (path: string, view: string, tab: string = 'water', slug?: string) => {
      const localizedPath = getLocalizedPath(path, language);
      if (typeof window !== 'undefined' && window.location.pathname !== localizedPath) {
        window.history.pushState({}, '', localizedPath);
      }
      setActiveView(view);
      setActiveTab(tab);
      if (slug) {
        setActiveSlug(slug);
      } else if (view === 'blog-article') {
        const cleanP = getPathWithoutLanguage(path).toLowerCase().replace(/\/+$/, '');
        const extracted = cleanP.replace(/^\/?blog\/?/, '').replace(/\/+$/, '');
        if (extracted) setActiveSlug(extracted);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [language]
  );

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 2500);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleToggleTheme = () => {
    setIsDarkMode((current) => !current);
  };

  // Dedicated Report Handlers (Strictly Isolated Per Calculator)
  const handleWaterReportChange = useCallback((data: WaterPipeReportData | null) => {
    setActiveReport((current) => {
      if (data) return { type: 'water', data };
      return current?.type === 'water' ? null : current;
    });
  }, []);

  const handleCopperReportChange = useCallback((data: CopperPipeReportData | null) => {
    setActiveReport((current) => {
      if (data) return { type: 'copper', data };
      return current?.type === 'copper' ? null : current;
    });
  }, []);

  const handleFlowReportChange = useCallback((data: FlowRateReportData | null) => {
    setActiveReport((current) => {
      if (data) return { type: 'flow', data };
      return current?.type === 'flow' ? null : current;
    });
  }, []);

  const handlePressureReportChange = useCallback((data: PressureDropReportData | null) => {
    setActiveReport((current) => {
      if (data) return { type: 'pressure', data };
      return current?.type === 'pressure' ? null : current;
    });
  }, []);

  const handleVelocityReportChange = useCallback((data: VelocityReportData | null) => {
    setActiveReport((current) => {
      if (data) return { type: 'velocity', data };
      return current?.type === 'velocity' ? null : current;
    });
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#F1F6F9] dark:bg-[#111827] text-slate-800 dark:text-slate-100 font-sans antialiased">
      <div className="no-print flex-1 flex flex-col">
        {toastMessage && (
          <div className="fixed bottom-5 right-5 z-50 bg-[#10283D] text-white text-xs px-4 py-2.5 rounded-xl shadow-lg border border-slate-700 font-medium flex items-center gap-2 animate-bounce">
            <span className="text-[#39BFD3]">✓</span>
            <span>{toastMessage}</span>
          </div>
        )}

        <Header
          unitSystem={unitSystem}
          setUnitSystem={setUnitSystem}
          activeView={activeView}
          setActiveView={setActiveView}
          onNavigate={navigateTo}
          onPrint={handlePrint}
          onShowToast={showToast}
          isDarkMode={isDarkMode}
          onToggleTheme={handleToggleTheme}
        />

        <main className="flex-1">
          {activeView === 'calculators' && (
            <div>
              {/* Header Section: Customized for Homepage vs Dedicated Calculator Pages */}
              <section className="bg-gradient-to-b from-white via-white to-[#F1F6F9] dark:from-[#1F2937] dark:via-[#1F2937] dark:to-[#111827] border-b border-slate-200/80 dark:border-slate-700/80 pt-8 pb-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  {/* Homepage Hero (when on default Water Pipe Sizing) */}
                  {activeTab === 'water' ? (
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                      <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 dark:bg-teal-950/40 border border-teal-200/60 dark:border-teal-800 text-[#087F9C] dark:text-[#39BFD3] text-xs font-semibold mb-3 max-w-full">
                          <span className="truncate sm:whitespace-normal">
                            {tx.standardsBadge}: BS EN 806 • CIBSE Guide G • ASME B36.10M • ASTM B88
                          </span>
                        </div>

                        <h1 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#10283D] dark:text-white tracking-tight leading-tight">
                          {tx.titleStart}{' '}
                          <span className="text-[#087F9C]">{tx.titleHighlight}</span>{' '}
                          {tx.titleEnd}
                        </h1>

                        <p className="mt-3 text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
                          {tx.heroDescription}
                        </p>
                      </div>

                      <div className="bg-white dark:bg-[#182332] p-4 sm:p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm lg:w-80 shrink-0">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-2">
                          ⚡ {tx.quickPresets}
                        </span>

                        <div className="grid grid-cols-1 gap-2 text-xs">
                          <button
                            onClick={() => {
                              navigateTo('/', 'calculators', 'water');
                              showToast(tx.toastDomestic);
                            }}
                            className="text-left px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/80 hover:bg-teal-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 font-medium text-slate-700 dark:text-slate-200 flex items-center justify-between"
                          >
                            <span>🚿 {tx.domesticWater}</span>
                            <span className="text-[#087F9C] font-semibold">{tx.apply} →</span>
                          </button>

                          <button
                            onClick={() => {
                              navigateTo('/', 'calculators', 'water');
                              showToast(tx.toastChilled);
                            }}
                            className="text-left px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/80 hover:bg-teal-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 font-medium text-slate-700 dark:text-slate-200 flex items-center justify-between"
                          >
                            <span>❄️ {tx.chilledWater}</span>
                            <span className="text-[#087F9C] font-semibold">{tx.apply} →</span>
                          </button>

                          <button
                            onClick={() => {
                              navigateTo('/', 'calculators', 'water');
                              showToast(tx.toastHighRise);
                            }}
                            className="text-left px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/80 hover:bg-teal-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 font-medium text-slate-700 dark:text-slate-200 flex items-center justify-between"
                          >
                            <span>🏢 {tx.highRise}</span>
                            <span className="text-[#087F9C] font-semibold">{tx.apply} →</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Dedicated Page Header with Breadcrumbs for other calculators */
                    <div>
                      <nav className="flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400 mb-3">
                        <button
                          onClick={() => navigateTo('/', 'calculators', 'water')}
                          className="hover:text-[#087F9C]"
                        >
                          Home
                        </button>
                        <span>/</span>
                        <span className="text-slate-400">Calculators</span>
                        <span>/</span>
                        <span className="text-[#087F9C] font-bold">
                          {activeTab === 'copper' && 'Copper Pipe Sizing'}
                          {activeTab === 'flow' && 'Flow Rate Calculator'}
                          {activeTab === 'pressure' && 'Pressure Drop (Darcy)'}
                          {activeTab === 'velocity' && 'Pipe Velocity & Standards'}
                        </span>
                      </nav>

                      {activeTab === 'copper' && (
                        <div>
                          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 dark:bg-amber-950/40 border border-amber-200/60 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-xs font-semibold mb-2">
                            <span>BS EN 1057 • ASTM B88 Plumbing Sizing Engine</span>
                          </div>
                          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#10283D] dark:text-white tracking-tight">
                            Copper Pipe Sizing Calculator
                          </h1>
                          <p className="mt-2 text-sm sm:text-base text-slate-600 dark:text-slate-300 max-w-3xl leading-relaxed">
                            Size commercial copper pipe diameter for domestic hot &amp; cold water, primary heating, and chilled water. Enforces CIBSE erosion limits across 15mm, 22mm, 28mm, 35mm, 42mm, and 54mm.
                          </p>
                        </div>
                      )}

                      {activeTab === 'flow' && (
                        <div>
                          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-50 dark:bg-cyan-950/40 border border-cyan-200/60 dark:border-cyan-800 text-[#087F9C] dark:text-cyan-300 text-xs font-semibold mb-2">
                            <span>Dual-Mode Continuity &amp; Pressure Inversion</span>
                          </div>
                          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#10283D] dark:text-white tracking-tight">
                            Pipe Flow Rate &amp; Capacity Calculator
                          </h1>
                          <p className="mt-2 text-sm sm:text-base text-slate-600 dark:text-slate-300 max-w-3xl leading-relaxed">
                            Compute volumetric flow rate and pipe capacity from fluid velocity or allowable friction loss across Litres/sec, m³/h, and US/UK GPM.
                          </p>
                        </div>
                      )}

                      {activeTab === 'pressure' && (
                        <div>
                          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-950/40 border border-blue-200/60 dark:border-blue-800 text-blue-800 dark:text-blue-300 text-xs font-semibold mb-2">
                            <span>Swamee-Jain Darcy-Weisbach Engine</span>
                          </div>
                          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#10283D] dark:text-white tracking-tight">
                            Darcy-Weisbach Pressure Drop Calculator
                          </h1>
                          <p className="mt-2 text-sm sm:text-base text-slate-600 dark:text-slate-300 max-w-3xl leading-relaxed">
                            Calculate Darcy friction factor, minor fitting losses, hydraulic gradient, and total head loss across commercial carbon steel, copper, PVC, and PEX piping.
                          </p>
                        </div>
                      )}

                      {activeTab === 'velocity' && (
                        <div>
                          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/60 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 text-xs font-semibold mb-2">
                            <span>CIBSE Guide G &amp; BS EN 806 Flow Criteria</span>
                          </div>
                          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#10283D] dark:text-white tracking-tight">
                            Pipe Flow Velocity &amp; Standards Calculator
                          </h1>
                          <p className="mt-2 text-sm sm:text-base text-slate-600 dark:text-slate-300 max-w-3xl leading-relaxed">
                            Verify internal fluid velocity, velocity head, and dynamic pressure against acoustic noise and erosion limits (0.5 – 2.4 m/s).
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Horizontal Tabs for Quick Calculator Switching */}
                  <div className="mt-8 border-b border-slate-200 dark:border-slate-700 flex overflow-x-auto gap-2 pb-px">
                    <button
                      onClick={() => navigateTo('/', 'calculators', 'water')}
                      className={`px-4 py-2.5 rounded-t-xl font-display font-bold text-sm flex items-center gap-2 whitespace-nowrap transition-all ${
                        activeTab === 'water'
                          ? 'border-b-2 border-[#087F9C] text-[#087F9C] bg-white dark:bg-[#1F2937] shadow-xs'
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      {tx.tabWater}
                    </button>

                    <button
                      onClick={() => navigateTo('/copper-pipe-sizing/', 'calculators', 'copper')}
                      className={`px-4 py-2.5 rounded-t-xl font-display font-bold text-sm flex items-center gap-2 whitespace-nowrap transition-all ${
                        activeTab === 'copper'
                          ? 'border-b-2 border-[#087F9C] text-[#087F9C] bg-white dark:bg-[#1F2937] shadow-xs'
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      {tx.tabCopper}
                    </button>

                    <button
                      onClick={() => navigateTo('/flow-rate-calculator/', 'calculators', 'flow')}
                      className={`px-4 py-2.5 rounded-t-xl font-display font-bold text-sm flex items-center gap-2 whitespace-nowrap transition-all ${
                        activeTab === 'flow'
                          ? 'border-b-2 border-[#087F9C] text-[#087F9C] bg-white dark:bg-[#1F2937] shadow-xs'
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      {tx.tabFlow}
                    </button>

                    <button
                      onClick={() => navigateTo('/pressure-drop-calculator/', 'calculators', 'pressure')}
                      className={`px-4 py-2.5 rounded-t-xl font-display font-bold text-sm flex items-center gap-2 whitespace-nowrap transition-all ${
                        activeTab === 'pressure'
                          ? 'border-b-2 border-[#087F9C] text-[#087F9C] bg-white dark:bg-[#1F2937] shadow-xs'
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      {tx.tabPressure}
                    </button>

                    <button
                      onClick={() => navigateTo('/pipe-velocity-standards/', 'calculators', 'velocity')}
                      className={`px-4 py-2.5 rounded-t-xl font-display font-bold text-sm flex items-center gap-2 whitespace-nowrap transition-all ${
                        activeTab === 'velocity'
                          ? 'border-b-2 border-[#087F9C] text-[#087F9C] bg-white dark:bg-[#1F2937] shadow-xs'
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      {tx.tabVelocity}
                    </button>
                  </div>
                </div>
              </section>

              {/* Active Calculator Main Section */}
              <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {activeTab === 'water' && (
                  <>
                    <WaterPipeCalculator
                      unitSystem={unitSystem}
                      onShowToast={showToast}
                      onPrint={handlePrint}
                      onReportDataChange={handleWaterReportChange}
                    />

                    {/* Section 1: Exactly four animated calculator cards immediately below Water Pipe Sizing */}
                    <ExploreCalculatorsSection
                      onNavigate={(path, tab) => navigateTo(path, 'calculators', tab)}
                      filterOutTab="water"
                    />
                  </>
                )}

                {activeTab === 'copper' && (
                  <>
                    <CopperPipeCalculator
                      unitSystem={unitSystem}
                      onPrint={handlePrint}
                      onReportDataChange={handleCopperReportChange}
                    />

                    <ExploreCalculatorsSection
                      title="Explore Other Calculators"
                      subtitle="Switch to other specialized hydraulic engineering tools"
                      onNavigate={(path, tab) => navigateTo(path, 'calculators', tab)}
                      filterOutTab="copper"
                    />
                  </>
                )}

                {activeTab === 'flow' && (
                  <>
                    <FlowRateCalculator
                      onPrint={handlePrint}
                      onReportDataChange={handleFlowReportChange}
                    />

                    <ExploreCalculatorsSection
                      title="Explore Other Calculators"
                      subtitle="Switch to other specialized hydraulic engineering tools"
                      onNavigate={(path, tab) => navigateTo(path, 'calculators', tab)}
                      filterOutTab="flow"
                    />
                  </>
                )}

                {activeTab === 'pressure' && (
                  <>
                    <PressureDropCalculator
                      onPrint={handlePrint}
                      onReportDataChange={handlePressureReportChange}
                    />

                    <ExploreCalculatorsSection
                      title="Explore Other Calculators"
                      subtitle="Switch to other specialized hydraulic engineering tools"
                      onNavigate={(path, tab) => navigateTo(path, 'calculators', tab)}
                      filterOutTab="pressure"
                    />
                  </>
                )}

                {activeTab === 'velocity' && (
                  <>
                    <VelocityCalculator
                      onPrint={handlePrint}
                      onReportDataChange={handleVelocityReportChange}
                    />

                    <ExploreCalculatorsSection
                      title="Explore Other Calculators"
                      subtitle="Switch to other specialized hydraulic engineering tools"
                      onNavigate={(path, tab) => navigateTo(path, 'calculators', tab)}
                      filterOutTab="velocity"
                    />
                  </>
                )}
              </section>

              {/* Mobile PDF Print/Report Button (Screens below 640px) */}
              <div className="sm:hidden px-4 pb-8 -mt-2">
                <button
                  type="button"
                  onClick={handlePrint}
                  className="w-full py-3.5 px-5 rounded-2xl font-bold text-sm bg-[#10283D] hover:bg-[#1E3A5F] active:bg-[#087F9C] text-white shadow-md flex items-center justify-center gap-2.5 transition-all duration-200 border border-slate-700/30"
                >
                  <svg
                    className="w-5 h-5 text-[#39BFD3]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                    />
                  </svg>
                  <span>PDF Print / Report</span>
                </button>
              </div>
            </div>
          )}

          {activeView === 'tables' && <ReferenceTables />}

          {activeView === 'guides' && <EngineeringGuides />}

          {activeView === 'formula' && <ArticleFormula />}

          {activeView === 'darcy' && <ArticleDarcy />}

          {activeView === 'blog-home' && (
            <BlogHomePage
              onNavigate={(path, view, tab) => navigateTo(path, view, tab || 'water')}
            />
          )}

          {activeView === 'blog-article' && (
            <BlogArticlePage
              article={getArticleBySlug(activeSlug) || getAllArticles()[0]}
              onNavigate={(path, view, tab) => navigateTo(path, view, tab || 'water')}
            />
          )}

          {activeView === 'about' && <AboutPage onNavigate={navigateTo} />}

          {activeView === 'contact' && <ContactPage onNavigate={navigateTo} />}

          {activeView === 'privacy' && <PrivacyPolicyPage onNavigate={navigateTo} />}

          {activeView === 'terms' && <TermsPage onNavigate={navigateTo} />}

          {activeView === 'disclaimer' && <DisclaimerPage onNavigate={navigateTo} />}
        </main>

        <Footer
          setActiveView={setActiveView}
          setActiveTab={setActiveTab}
          onNavigate={navigateTo}
        />
      </div>

      <PrintableReport reportData={activeReport} />
    </div>
  );
}
